import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton } from './Button'
import Checkbox from './Checkbox'
import DateSelector from './DateSelector'
import { InputLabel } from './Input'
import Modal, { ModalFooter } from './Modal'
import SegmentedControl from './SegmentedControl'
import Switch from './Switch'
import Radom, { CreateReportRequest, ReportFormat, ReportKind, ReportView } from '../state/Radom'
import { errorToast, useTestMode } from '../util/Util'
import { InlineNotice } from './DashboardPrimitives'
import { ReportKindGlyph } from './ReportKindGlyph'
import { RowIconCircle } from '../views/Money/MoneyStyles'

interface ReportOption {
  kind: ReportKind
  title: string
  description: string
  formats: ReportFormat[]
}

export const REPORT_OPTIONS: ReportOption[] = [
  {
    kind: 'statement',
    title: 'Account statement',
    description: 'Balances and every movement between them, per asset.',
    formats: ['pdf', 'csv']
  },
  {
    kind: 'transactions',
    title: 'Transactions',
    description: 'Every balance movement as one line, for bookkeeping.',
    formats: ['pdf', 'csv']
  },
  {
    kind: 'payments',
    title: 'Payments received',
    description: 'Customer payments with fees and the net of each.',
    formats: ['pdf', 'csv']
  },
  {
    kind: 'payouts',
    title: 'Payouts & withdrawals',
    description: 'Everything you sent out, by recipient.',
    formats: ['pdf', 'csv']
  },
  {
    kind: 'fees',
    title: 'Fees summary',
    description: 'What you were charged, by month and kind of fee.',
    formats: ['pdf', 'csv']
  }
]

type PeriodPreset = 'lastMonth' | 'last3Months' | 'last6Months' | 'yearToDate' | 'custom'

const PERIODS: Array<{ id: PeriodPreset, label: string }> = [
  { id: 'lastMonth', label: 'Last month' },
  { id: 'last3Months', label: '3 months' },
  { id: 'last6Months', label: '6 months' },
  { id: 'yearToDate', label: 'This year' },
  { id: 'custom', label: 'Custom' }
]

const FORMATS: Array<{ id: ReportFormat | 'both', label: string }> = [
  { id: 'pdf', label: 'PDF' },
  { id: 'csv', label: 'CSV' },
  { id: 'both', label: 'Both' }
]

// The balance ledger behind statements starts here; earlier days have nothing to report.
const STATEMENTS_FROM = new Date(2025, 11, 1)
const REPORTS_FROM = new Date(2020, 0, 1)

/** Start of a UTC month, `offset` months from the month `now` is in. */
const utcMonthStart = (now: Date, offset: number): Date =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1))

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "1 Sep 2026" for a UTC instant. */
const utcDayLabel = (d: Date): string => `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`

/** The UTC day a date-picker value refers to, at 00:00. */
const utcDay = (d: Date): Date => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))

export const periodFor = (
  preset: PeriodPreset,
  now: Date,
  custom: { from?: Date, to?: Date }
): { from: Date, to: Date } | null => {
  switch (preset) {
  case 'lastMonth':
    return { from: utcMonthStart(now, -1), to: utcMonthStart(now, 0) }
  case 'last3Months':
    return { from: utcMonthStart(now, -3), to: utcMonthStart(now, 0) }
  case 'last6Months':
    return { from: utcMonthStart(now, -6), to: utcMonthStart(now, 0) }
  case 'yearToDate':
    return { from: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)), to: now }
  case 'custom': {
    if (!custom.from || !custom.to) {
      return null
    }
    const from = utcDay(custom.from)
    // The picker gives the last day covered; the API takes an exclusive end.
    const to = new Date(utcDay(custom.to).getTime() + 24 * 60 * 60 * 1000)
    return { from, to }
  }
  }
}

// A statement is a document to read; the other reports are data first, so they come as both files.
const defaultFormat = (kind: ReportKind): ReportFormat | 'both' => kind === 'statement' ? 'pdf' : 'both'

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 2px 0 4px;
  width: 100%;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// One grouped list, like the account lists on the Money home: the chosen row carries the mark.
const OptionList = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const OptionRow = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-top: 1px solid var(--hairline, #eef1f5);
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  gap: 12px;
  justify-content: space-between;
  padding: 9px 12px;
  text-align: left;
  transition: background-color 0.14s ease;
  width: 100%;

  &:first-child {
    border-top: 0;
  }

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    background: var(--surface-pressed, #eef1f7);
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const OptionLead = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 12px;
  min-width: 0;
`

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const OptionTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
`

const OptionDescription = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.4;
`

const DateGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  padding-top: 2px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

// Sits in the footer beside the action it qualifies, so it is on screen whatever the
// viewport height; the whole row is the control.
const NotifyRow = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 0 2px;
  user-select: none;

  &:focus-visible {
    border-radius: 8px;
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const SwitchText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

interface Props {
  visible: boolean
  onClose: () => void
  onCreated: (report: ReportView) => void
  initialKind?: ReportKind
  maxPeriodDays?: number
}

const RequestReportDrawer = (props: Props): ReactElement => {
  const { visible, onClose, onCreated, initialKind, maxPeriodDays = 366 } = props
  const [kind, setKind] = useState<ReportKind>(initialKind ?? 'statement')
  const [period, setPeriod] = useState<PeriodPreset>('lastMonth')
  const [customFrom, setCustomFrom] = useState<Date>()
  const [customTo, setCustomTo] = useState<Date>()
  const [format, setFormat] = useState<ReportFormat | 'both'>(defaultFormat(initialKind ?? 'statement'))
  const [notify, setNotify] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [isTestMode] = useTestMode()

  useEffect(() => {
    if (visible) {
      setKind(initialKind ?? 'statement')
      setPeriod('lastMonth')
      setCustomFrom(undefined)
      setCustomTo(undefined)
      setFormat(defaultFormat(initialKind ?? 'statement'))
      setNotify(true)
    }
  }, [visible, initialKind])

  const today = useMemo(() => new Date(), [visible])
  const earliest = kind === 'statement' ? STATEMENTS_FROM : REPORTS_FROM
  const range = useMemo(
    () => periodFor(period, new Date(), { from: customFrom, to: customTo }),
    [period, customFrom, customTo]
  )

  // Only a complete choice can be wrong: an unfinished custom period is a hint, not an error.
  const rangeError = useMemo((): string | null => {
    if (!range) {
      return null
    }
    if (range.to <= range.from) {
      return 'The period must end after it starts.'
    }
    const days = (range.to.getTime() - range.from.getTime()) / (24 * 60 * 60 * 1000)
    if (days > maxPeriodDays) {
      return `A report can cover at most ${maxPeriodDays} days.`
    }
    if (kind === 'statement' && range.from < new Date(Date.UTC(2025, 11, 1))) {
      return 'Statements are available from December 2025.'
    }
    return null
  }, [range, kind, maxPeriodDays])

  const submit = async (): Promise<void> => {
    if (!range || rangeError) {
      return
    }
    const formats: ReportFormat[] = format === 'both' ? ['pdf', 'csv'] : [format]
    const req: CreateReportRequest = {
      kind,
      periodStart: range.from.toISOString(),
      periodEnd: range.to.toISOString(),
      formats,
      isTestMode: kind === 'payouts' ? isTestMode : false,
      notifyByEmail: notify
    }
    setSubmitting(true)
    try {
      const created = await Radom.createReport(req)
      onCreated(created)
    } catch (err: any) {
      errorToast(err?.response?.data?.error ?? err?.message ?? 'The report could not be requested')
    } finally {
      setSubmitting(false)
    }
  }

  const periodLabel = PERIODS.find(p => p.id === period)?.label ?? PERIODS[0].label
  const formatLabel = FORMATS.find(f => f.id === format)?.label ?? FORMATS[0].label

  return <Modal visible={visible} onClose={onClose} presentation='drawer' title='Request a report'>
    <Composer>
      <Section>
        <InputLabel>What do you need?</InputLabel>
        <OptionList role='radiogroup' aria-label='Report type'>
          {REPORT_OPTIONS.map(o =>
            <OptionRow
              key={o.kind}
              type='button'
              role='radio'
              aria-checked={o.kind === kind}
              onClick={() => { setKind(o.kind); setFormat(defaultFormat(o.kind)) }}>
              <OptionLead>
                <RowIconCircle $muted={o.kind !== kind} style={{ height: 32, width: 32 }}>
                  <ReportKindGlyph kind={o.kind} />
                </RowIconCircle>
                <OptionText>
                  <OptionTitle>{o.title}</OptionTitle>
                  <OptionDescription>{o.description}</OptionDescription>
                </OptionText>
              </OptionLead>
              <Checkbox isRadio checked={o.kind === kind} size={20} />
            </OptionRow>
          )}
        </OptionList>
      </Section>

      <Section>
        <InputLabel>Period</InputLabel>
        <SegmentedControl
          fill
          tabs={PERIODS.map(p => p.label)}
          selectedTab={periodLabel}
          onTabChange={label => setPeriod(PERIODS.find(p => p.label === label)?.id ?? 'lastMonth')} />
        {period === 'custom' &&
          <DateGrid>
            <DateField>
              <InputLabel>From</InputLabel>
              <DateSelector
                ariaLabel='First day to include'
                value={customFrom}
                onChange={setCustomFrom}
                startDate={earliest}
                endDate={today} />
            </DateField>
            <DateField>
              <InputLabel>To</InputLabel>
              <DateSelector
                ariaLabel='Last day to include'
                value={customTo}
                onChange={setCustomTo}
                startDate={customFrom ?? earliest}
                endDate={today}
                initialDate={customFrom} />
            </DateField>
          </DateGrid>}
        {rangeError
          ? <InlineNotice role='alert' style={{ color: 'var(--pill-red-ink, #c51735)' }}>{rangeError}</InlineNotice>
          : range
            ? <InlineNotice>
              Covers {utcDayLabel(range.from)} – {utcDayLabel(new Date(range.to.getTime() - 1))} (UTC)
            </InlineNotice>
            : <InlineNotice>Choose the first and last day to include.</InlineNotice>}
      </Section>

      <Section>
        <InputLabel>Format</InputLabel>
        <div>
          <SegmentedControl
            tabs={FORMATS.map(f => f.label)}
            selectedTab={formatLabel}
            onTabChange={label => setFormat(FORMATS.find(f => f.label === label)?.id ?? 'pdf')} />
        </div>
        {kind !== 'statement' && format !== 'csv' &&
          <InlineNotice>A PDF is prepared for up to 5,000 rows; a longer report comes as CSV only.</InlineNotice>}
      </Section>

    </Composer>
    <ModalFooter $fullWidthAction style={{ flexDirection: 'column', gap: 12 }}>
      <NotifyRow
        role='switch'
        aria-checked={notify}
        tabIndex={0}
        onClick={() => setNotify(n => !n)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setNotify(n => !n)
          }
        }}>
        <SwitchText>
          <OptionTitle>Email me when it&apos;s ready</OptionTitle>
          <OptionDescription>The email links back here and contains no figures.</OptionDescription>
        </SwitchText>
        <Switch checked={notify} sizeScale={0.85} />
      </NotifyRow>
      <PrimaryButton isLoading={submitting} disabled={!!rangeError || !range} onClick={() => { void submit() }}>
        Prepare report
      </PrimaryButton>
    </ModalFooter>
  </Modal>
}

export default RequestReportDrawer
